import { ChangeDetectorRef, Directive, ElementRef, HostListener, inject, Input, OnChanges, OnInit, Renderer2, SimpleChanges } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { UserService } from '@core/services/user.service';
import { ImageConversionService } from '@core/services';

@UntilDestroy()
@Directive({
  selector: '[appFetchProfileImage]',
  standalone: true,
})

/**
 * Fetches the profile image of a user and sets it as the source of the image element.
 * If the image is not found, a fallback image is used.
 * Only works with `<img>` elements.
 *
 * @requires memberId: string | number - The ID of the user whose profile image is to be fetched.
 *
 * @example
 * <img [appFetchProfileImage]="memberId" /> OR  <img appFetchProfileImage [memberId]="memberId" />
 * <img [appFetchProfileImage]="memberId" [fallbackImage]="fallbackImage" />
 *
 */
export class FetchProfileImageDirective implements OnInit, OnChanges {
  @Input('appFetchProfileImage') memberId: string | number;
  @Input() fallbackImage = 'assets/images/placeholder.png';
  @Input() fetchFromLocal = false;
  private readonly _userService = inject(UserService);
  private readonly _imageConversionService = inject(ImageConversionService);
  private readonly _el = inject(ElementRef);
  private readonly _cdr = inject(ChangeDetectorRef);
  private readonly _renderer = inject(Renderer2);
  private readonly _loadingClasses = ['background-loading-tiny', 'background-loading-small', 'background-loading'];

  ngOnInit() {
    if (!+this.memberId) {
      this._setFallbackImage();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['memberId'] && +this.memberId) {
      this._fetchImage();
    } else if (!+this.memberId) {
      this._setFallbackImage();
    }
  }

  @HostListener('error')
  onError() {
    this._setFallbackImage();
  }

  @HostListener('load')
  onLoad() {
    this._removeBackgroundLoadingClasses();
  }

  private _fetchImage() {
    if (!this.memberId) {
      this._setFallbackImage();
      return;
    }

    if (this.fetchFromLocal) {
      this._fetchFromLocal();
    } else {
      this._fetchFromRemote();
    }
  }

  private _setFallbackImage() {
    this._el.nativeElement.src = this.fallbackImage;
  }

  private _removeBackgroundLoadingClasses() {
    const parentElement = this._el.nativeElement.parentElement;
    if (parentElement) {
      this._loadingClasses.forEach((className) => {
        if (parentElement.classList.contains(className)) {
          this._renderer.removeClass(parentElement, className);
        }
      });
    }
  }

  private _fetchFromLocal() {
    this._userService
      .getUserProfilePhotoFromLocal(+this.memberId)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (resp) => {
          if (resp) {
            this._el.nativeElement.src = this._imageConversionService.convertBase64ToBlobUrl(resp);
            this._cdr.markForCheck();
          } else {
            this._setFallbackImage(); // Handle empty blobs
          }
        },
        error: () => {
          this._setFallbackImage();
        },
      });
  }

  private _fetchFromRemote() {
    this._userService
      .getUserProfilePhoto(+this.memberId)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (resp) => {
          if (resp) {
            this._el.nativeElement.src = this._imageConversionService.convertBase64ToBlobUrl(resp);
            this._cdr.markForCheck();
          } else {
            this._setFallbackImage(); // Handle empty blobs
          }
        },
        error: () => {
          this._setFallbackImage();
        },
      });
  }
}
